/**
 * /frontend/src/features/analytics/utils/dateUtils.js
 */

/**
 * Formats a JS Date to YYYY-MM-DD (local).
 * @param {Date} date
 * @returns {string}
 */
export const toIsoDate = (date) => {
  const pad = (value) => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

/**
 * Adds days to a date without mutating the original.
 * @param {Date} date
 * @param {number} days
 * @returns {Date}
 */
export const addDays = (date, days) => new Date(date.getTime() + (days * 24 * 60 * 60 * 1000));

/**
 * Returns today's date in YYYY-MM-DD (local).
 * @returns {string}
 */
export const todayIsoDate = () => toIsoDate(new Date());

/**
 * Checks if a string is a valid ISO date (YYYY-MM-DD).
 * @param {unknown} value
 * @returns {boolean}
 */
export const isIsoDateString = (value) => {
  if (typeof value !== 'string') return false;
  const trimmed = value.trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return false;
  const parsed = new Date(`${trimmed}T00:00:00`);
  return !Number.isNaN(parsed.getTime());
};

/**
 * Compares ISO dates (YYYY-MM-DD). Returns -1, 0, 1.
 * @param {string} a
 * @param {string} b
 * @returns {number}
 */
export const compareIsoDates = (a, b) => {
  if (!isIsoDateString(a) || !isIsoDateString(b)) return 0;
  if (a === b) return 0;
  return a < b ? -1 : 1;
};

/**
 * Clamps an ISO date within optional min/max bounds.
 * @param {string} value
 * @param {{min?:string,max?:string}} bounds
 * @returns {string}
 */
export const clampIsoDate = (value, bounds = {}) => {
  const { min, max } = bounds;
  if (!isIsoDateString(value)) return '';
  let result = value;
  if (isIsoDateString(min) && compareIsoDates(result, min) < 0) result = min;
  if (isIsoDateString(max) && compareIsoDates(result, max) > 0) result = max;
  return result;
};

/**
 * Validates a start/end ISO date range.
 * @param {string} startDate
 * @param {string} endDate
 * @returns {{valid:boolean,message:string}}
 */
export const validateIsoDateRange = (startDate, endDate) => {
  if (!isIsoDateString(startDate) || !isIsoDateString(endDate)) {
    return { valid: false, message: 'Select a valid start and end date.' };
  }
  if (compareIsoDates(startDate, endDate) > 0) {
    return { valid: false, message: 'Start date cannot be after end date.' };
  }
  return { valid: true, message: '' };
};
